import { useEffect, useMemo, useState } from "react";
import React from "react";
import { Card, Form } from "react-bootstrap";
import { Link } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import { LogAction, badgeClass, isISODate, isUrl, parseLogs, shouldPreserveNewlines } from "../../services/ParseLog";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { GenericLogsProps, LogEvent } from "./GenericLogs.d";

const ActionBadge = ({ action, language }: { action: LogAction; language: string }) => (
  <span className={`badge ${badgeClass[action]} text-uppercase`}>
    {translate(language, `COMPONENTS.GENERIC_LOGS.ACTIONS.${action.toUpperCase()}`)}
  </span>
);

const ValueCell = ({ value, language, attrKey }: { value: string | null; language: string; attrKey?: string }) => {
  if (!value) return <em className="text-muted">—</em>;

  if (shouldPreserveNewlines(attrKey)) {
    const text = String(value);
    const hasNewlines = /\r?\n/.test(text);
    const lines = text.split(/\r?\n/);

    return hasNewlines ? (
      <span className="d-block off-white-background border rounded p-2 text-break w-100">
        {
          <React.Fragment>
            {lines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </React.Fragment>
        }
      </span>
    ) : (
      <span className="text-break">{text}</span>
    );
  }

  if (typeof value === "string" && value.includes(",")) {
    const values = value.split(",").map((value) => value.trim());

    return (
      <ul className="mb-0 ps-3">
        {values.map((value, index) => (
          <li key={index}>
            {isUrl(value) ? (
              <a href={value} target="_blank" rel="noreferrer">
                {value}
              </a>
            ) : isISODate(value) ? (
              formatDateHumanly(language, value, true)
            ) : (
              value
            )}
          </li>
        ))}
      </ul>
    );
  }

  if (isUrl(value)) {
    return (
      <a href={value} target="_blank" rel="noreferrer">
        {value}
      </a>
    );
  }

  if (isISODate(value)) {
    return <span>{formatDateHumanly(language, value, true)}</span>;
  }

  return <span>{value}</span>;
};

const InlineItems = ({
  items,
  language,
  translationPrefix,
}: {
  items: { key: string; value: string | null }[];
  language: string;
  translationPrefix: string;
}) => (
  <ul className="list-unstyled m-0 d-flex flex-column gap-2">
    {items.map((item, index) => (
      <li key={`${item.key}-${index}`} className="d-flex flex-wrap gap-2">
        {translate(language, `${translationPrefix}.${item.key.toUpperCase()}`) || item.key}
        <span className="flex-grow-1">
          <ValueCell value={item.value} language={language} attrKey={item.key} />
        </span>
      </li>
    ))}
  </ul>
);

const FormattedLogs = ({
  content,
  language,
  timestamp,
  mock,
  translationPrefix,
}: {
  content: string;
  language: string;
  timestamp: number;
  mock?: boolean;
  translationPrefix: string;
}) => {
  const logs = useMemo(() => parseLogs(content, language), [content, language]);

  if (mock) {
    return (
      <Card className="s-regular shadow-sm border-0">
        <Card.Body className="d-flex flex-column gap-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="placeholder rounded-pill" style={{ width: 70, height: 20 }} />
              <span className="placeholder" style={{ width: 30, height: 14 }} />
              <span className="placeholder fw-semibold" style={{ width: 100, height: 16 }} />
            </div>
            <span className="placeholder" style={{ width: 120, height: 14 }} />
          </div>

          <div className="d-flex flex-column gap-2">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="d-flex flex-wrap gap-2">
                <span className="placeholder" style={{ width: 110, height: 14 }} />
                <span className="placeholder flex-grow-1" style={{ height: 14 }} />
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!logs.length) {
    return (
      <Card className="s-regular">
        <Card.Body>{content}</Card.Body>
      </Card>
    );
  }

  return (
    <div className="d-grid gap-3">
      {logs.map((log, index) => (
        <Card key={`${timestamp}-${index}`} className="s-regular">
          <Card.Body>
            <div className="d-flex justify-content-between mb-2 flex-wrap">
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <ActionBadge action={log.action} language={language} />
                <span className="text-muted">{translate(language, "COMPONENTS.GENERIC_LOGS.ACTIONS.BY")}</span>
                <Link to={`/user/${log.authorId}`} className="fw-semibold">
                  {log.authorId}
                </Link>
              </div>

              <span className="text-muted small">
                {formatDateHumanly(language, new Date(timestamp).toISOString(), true)}
              </span>
            </div>

            <InlineItems items={log.items} language={language} translationPrefix={translationPrefix} />
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

const DetailedFormattedLogs = ({
  event,
  language,
  translationPrefix,
  transform,
}: {
  event: any;
  language: string;
  translationPrefix: string;
  transform?: (
    event: any,
    language: string
  ) => {
    items: { key: string; value: string | null }[];
  };
}) => {
  const _formatDetailedLogValue = (value: any) => {
    if (value === "True") return translate(language, "COMPONENTS.GENERIC_LOGS.VALUES.TRUE");
    if (value === "False") return translate(language, "COMPONENTS.GENERIC_LOGS.VALUES.FALSE");
    return value;
  };

  let items = [];

  if (transform) {
    items =
      transform(event, language)?.items?.map((item) => ({
        ...item,
        value: _formatDetailedLogValue(item.value),
      })) || [];
  } else {
    const types = event.target?.type?.split(",") || [];

    items = types.map((type: string, index: number) => ({
      key: event.target.items[index]?.name || type,
      value: _formatDetailedLogValue(event.target.items[index]?.id ?? null),
    }));
  }

  return (
    <Card className="s-regular">
      <Card.Body>
        <div className="d-flex justify-content-between mb-2 flex-wrap">
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <ActionBadge action={event.action.toLowerCase()} language={language} />
            <span className="text-muted">{translate(language, "COMPONENTS.GENERIC_LOGS.ACTIONS.BY")}</span>
            <Link to={`/user/${event.user_id}`} className="fw-semibold">
              {event.author?.name || event.user_id}
            </Link>
          </div>
          <span className="text-muted small">
            {formatDateHumanly(language, new Date(event.timestamp).toISOString(), true)}
          </span>
        </div>
        <InlineItems items={items} language={language} translationPrefix={translationPrefix} />
      </Card.Body>
    </Card>
  );
};

export const GenericLogs = ({
  entityId,
  language,
  fetchLogs,
  translationPrefix,
  detailedConfig,
  filterLog,
  headerContent,
  transformDetailedLog,
}: GenericLogsProps) => {
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchLogs(entityId, setLogs, setLoaded, language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, language]);

  const filteredLogs = filterLog ? logs.filter((log) => filterLog(log)) : logs;

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {headerContent}
          {detailedConfig?.enabled && (
            <DstButton
              btnClass="d-flex align-items-center"
              btnImageBefore={
                <Form.Check type="switch" className="check-box s-bold me-2" checked={detailedConfig.value} />
              }
              value={translate(language, "COMPONENTS.GENERIC_LOGS.DETAILED")}
              variant="light"
              clickFunction={detailedConfig.onToggle}
            />
          )}
        </div>

        <DstButton
          btnClass="btn-secondary"
          btnImageBefore={<span className="dst-icon-refresh-ccw-02 pe-2 align-text-bottom" />}
          value={translate(language, "COMPONENTS.GENERIC_LOGS.REFRESH_BUTTON")}
          clickFunction={() => {
            fetchLogs(entityId, setLogs, setLoaded, language);
          }}
          loading={!loaded}
          disabled={!loaded}
        />
      </div>

      {!loaded ? (
        [...Array(3)].map((_, index) => (
          <FormattedLogs
            key={index}
            mock
            language={language}
            content=""
            timestamp={0}
            translationPrefix={translationPrefix}
          />
        ))
      ) : !filteredLogs.length ? (
        <Card>
          <Card.Body className="text-muted s-regular">
            {translate(language, "COMPONENTS.GENERIC_LOGS.NO_LOGS")}
          </Card.Body>
        </Card>
      ) : (
        filteredLogs.map((event, index) =>
          detailedConfig?.value ? (
            <DetailedFormattedLogs
              key={`${event.timestamp}-${index}`}
              event={event}
              language={language}
              translationPrefix={translationPrefix}
              transform={transformDetailedLog}
            />
          ) : (
            <FormattedLogs
              key={`${event.timestamp}-${index}`}
              content={event.content}
              timestamp={event.timestamp}
              language={language}
              translationPrefix={translationPrefix}
            />
          )
        )
      )}
    </div>
  );
};
